import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { toast } from 'sonner';
import { formatDate, toISODateString } from '../lib/utils';
import { Plus, Pencil, Trash2, CalendarIcon, Users, FileText, Clock, ClipboardList, Upload, Download, Check, X, UserPlus, KeyRound, ChevronLeft, ChevronRight, CalendarOff, ShieldAlert, FileWarning, Wallet } from 'lucide-react';
import { AbsencesTab } from '../components/AbsencesTab';
import { AvancesTab } from '../components/AvancesTab';
import { Textarea } from '../components/ui/textarea';
import { format, differenceInBusinessDays, parseISO, addMonths, subMonths, startOfMonth, getDaysInMonth, getDay, isWeekend, isSameMonth } from 'date-fns';
import { fr } from 'date-fns/locale';

function Avatar({ prenom, nom, couleur, size = 'md' }) {
  const initials = `${(prenom || '')[0] || ''}${(nom || '')[0] || ''}`.toUpperCase() || '?';
  const s = size === 'lg' ? 'w-14 h-14 text-xl' : size === 'sm' ? 'w-8 h-8 text-xs' : 'w-11 h-11 text-base';
  return (
    <div className={`${s} rounded-full flex items-center justify-center text-white font-bold shrink-0`} style={{ backgroundColor: couleur || '#6B7280' }}>
      {initials}
    </div>
  );
}

const TYPES_CONTRAT = ['CDI', 'CDD', 'Alternance', 'Stage', 'Freelance'];
const TYPES_CONGE = [
  { value: 'cp', label: 'Congé payé' },
  { value: 'rtt', label: 'RTT' },
  { value: 'maladie', label: 'Maladie' },
  { value: 'sans_solde', label: 'Sans solde' },
  { value: 'autre', label: 'Autre' },
];
const DOC_CATEGORIES = [
  { value: 'fiche_paie', label: 'Fiche de paie' },
  { value: 'contrat', label: 'Contrat' },
  { value: 'avenant', label: 'Avenant' },
  { value: 'attestation', label: 'Attestation' },
  { value: 'autre', label: 'Autre document' },
];

export function RHPage() {
  const { api, user } = useAuth();
  const [employes, setEmployes] = useState([]);
  const [conges, setConges] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [myProfile, setMyProfile] = useState(null);
  const [tab, setTab] = useState(() => user?.role !== 'admin' ? 'profil' : 'equipe');
  const [loading, setLoading] = useState(true);
  const isAdmin = user?.role === 'admin';
  const isEmployee = user?.role !== 'admin';

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [empRes, congesRes, docsRes] = await Promise.all([
        api.get('/rh/employes'),
        api.get('/rh/conges'),
        api.get('/rh/documents'),
      ]);
      setEmployes(empRes.data);
      setConges(congesRes.data);
      setDocuments(docsRes.data);
    } catch { toast.error('Erreur chargement RH'); }
    // Fetch profile separately for non-admins to avoid breaking Promise.all
    if (isEmployee) {
      try {
        const profileRes = await api.get('/rh/me');
        if (profileRes.data) setMyProfile(profileRes.data);
      } catch { /* profile not found */ }
    }
    setLoading(false);
  }, [api, isEmployee]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // For employees: default to 'profil' tab, hide equipe/registre
  useEffect(() => {
    if (isEmployee && (tab === 'equipe' || tab === 'registre')) setTab('profil');
  }, [isEmployee, tab]);

  const seedEmployes = async () => {
    try {
      const res = await api.post('/rh/seed');
      toast.success(res.data.message);
      fetchAll();
    } catch { toast.error('Erreur seed'); }
  };

  return (
    <div className="space-y-6">
      <div className="module-header">
        <div>
          <h1 className="module-title" data-testid="rh-title">RH</h1>
          <p className="text-muted-foreground mt-1">
            {isEmployee ? `Mon espace personnel` : 'Gestion des ressources humaines'}
          </p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className={`grid w-full max-w-3xl ${isEmployee ? 'grid-cols-4' : 'grid-cols-7'}`}>
          {!isEmployee && (
            <TabsTrigger value="equipe" className="gap-1.5" data-testid="tab-equipe"><Users className="w-4 h-4" /> Équipe</TabsTrigger>
          )}
          {isEmployee && (
            <TabsTrigger value="profil" className="gap-1.5" data-testid="tab-profil"><Users className="w-4 h-4" /> Mon Profil</TabsTrigger>
          )}
          <TabsTrigger value="conges" className="gap-1.5" data-testid="tab-conges"><CalendarIcon className="w-4 h-4" /> Congés</TabsTrigger>
          {!isEmployee && (
            <TabsTrigger value="absences" className="gap-1.5" data-testid="tab-absences"><CalendarOff className="w-4 h-4" /> Absences</TabsTrigger>
          )}
          {!isEmployee && (
            <TabsTrigger value="avances" className="gap-1.5" data-testid="tab-avances"><Wallet className="w-4 h-4" /> Avances</TabsTrigger>
          )}
          <TabsTrigger value="vacances" className="gap-1.5" data-testid="tab-vacances"><CalendarIcon className="w-4 h-4" /> Planning Vacances</TabsTrigger>
          <TabsTrigger value="documents" className="gap-1.5" data-testid="tab-documents"><FileText className="w-4 h-4" /> Documents</TabsTrigger>
          {!isEmployee && (
            <TabsTrigger value="registre" className="gap-1.5" data-testid="tab-registre"><ClipboardList className="w-4 h-4" /> Registre</TabsTrigger>
          )}
        </TabsList>

        {!isEmployee && (
          <TabsContent value="equipe">
            <EquipeTab employes={employes} api={api} onRefresh={fetchAll} onSeed={seedEmployes} isAdmin={isAdmin} />
          </TabsContent>
        )}
        {isEmployee && (
          <TabsContent value="profil">
            <MonProfilTab profile={myProfile} conges={conges} />
          </TabsContent>
        )}
        <TabsContent value="conges">
          <CongesTab conges={conges} employes={employes} api={api} onRefresh={fetchAll} isAdmin={isAdmin} user={user} myProfile={myProfile} />
        </TabsContent>
        {!isEmployee && (
          <TabsContent value="absences">
            <AbsencesTab employes={employes} api={api} isAdmin={isAdmin} />
          </TabsContent>
        )}
        {!isEmployee && (
          <TabsContent value="avances">
            <AvancesTab employes={employes} api={api} isAdmin={isAdmin} />
          </TabsContent>
        )}
        <TabsContent value="vacances">
          <VacancesCalendar api={api} employes={employes} />
        </TabsContent>
        <TabsContent value="documents">
          <DocumentsTab documents={documents} employes={employes} api={api} onRefresh={fetchAll} isAdmin={isAdmin} myProfile={myProfile} />
        </TabsContent>
        {!isEmployee && (
          <TabsContent value="registre">
            <RegistreTab employes={employes} conges={conges} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

// ============== EQUIPE TAB ==============
function EquipeTab({ employes, api, onRefresh, onSeed, isAdmin }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [accountDialog, setAccountDialog] = useState(null);
  const [haccpDialog, setHaccpDialog] = useState(null); // { emp, type } or null

  const handleSave = async (form) => {
    try {
      if (editItem) {
        await api.put(`/rh/employes/${editItem.id}`, form);
        toast.success('Employé modifié');
      } else {
        await api.post('/rh/employes', form);
        toast.success('Employé créé');
      }
      setDialogOpen(false);
      setEditItem(null);
      onRefresh();
    } catch { toast.error('Erreur'); }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/rh/employes/${id}`);
      toast.success('Employé supprimé');
      onRefresh();
    } catch { toast.error('Erreur'); }
  };

  const handleCreateAccount = async (emp, email, password) => {
    try {
      await api.post(`/rh/employes/${emp.id}/create-account`, { email, password });
      toast.success(`Compte créé pour ${emp.prenom}`);
      setAccountDialog(null);
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-end gap-2 flex-wrap">
        {employes.length === 0 && (
          <Button variant="outline" onClick={onSeed} data-testid="btn-seed-employes">
            Charger l'équipe initiale
          </Button>
        )}
        {isAdmin && (
          <>
            <Button variant="outline" className="gap-2" onClick={() => setHaccpDialog({ emp: null, type: 'rappel' })} data-testid="btn-rappel-haccp">
              <ShieldAlert className="w-4 h-4" /> Rappel HACCP
            </Button>
            <Button variant="outline" className="gap-2 border-red-300 text-red-700 hover:bg-red-50" onClick={() => setHaccpDialog({ emp: null, type: 'avertissement' })} data-testid="btn-avertissement">
              <FileWarning className="w-4 h-4" /> Avertissement
            </Button>
            <Button className="gap-2" onClick={() => { setEditItem(null); setDialogOpen(true); }} data-testid="btn-add-employe">
              <UserPlus className="w-4 h-4" /> Nouvel employé
            </Button>
          </>
        )}
      </div>

      {employes.length === 0 ? (
        <Card className="py-12">
          <CardContent className="text-center text-muted-foreground">
            <Users className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p className="text-lg font-medium">Aucun employé</p>
            <p className="text-sm mt-1">Ajoutez votre premier employé ou chargez l'équipe initiale</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {employes.map(emp => (
            <EmployeCard key={emp.id} emp={emp} isAdmin={isAdmin}
              onEdit={() => { setEditItem(emp); setDialogOpen(true); }}
              onDelete={() => handleDelete(emp.id)}
              onCreateAccount={() => setAccountDialog(emp)} />
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{editItem ? 'Modifier' : 'Nouvel'} employé</DialogTitle></DialogHeader>
          <EmployeForm item={editItem} onSave={handleSave} onClose={() => setDialogOpen(false)} />
        </DialogContent>
      </Dialog>

      <Dialog open={!!accountDialog} onOpenChange={() => setAccountDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Créer un compte pour {accountDialog?.prenom}</DialogTitle></DialogHeader>
          {accountDialog && <AccountForm emp={accountDialog} onSave={handleCreateAccount} onClose={() => setAccountDialog(null)} />}
        </DialogContent>
      </Dialog>

      <Dialog open={!!haccpDialog} onOpenChange={() => setHaccpDialog(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {haccpDialog?.type === 'avertissement' ? <FileWarning className="w-5 h-5 text-red-600" /> : <ShieldAlert className="w-5 h-5 text-amber-600" />}
              {haccpDialog?.type === 'avertissement' ? 'Avertissement HACCP' : 'Rappel HACCP'}
            </DialogTitle>
          </DialogHeader>
          {haccpDialog && <HaccpDocForm employes={employes} api={api} type={haccpDialog.type} onClose={() => setHaccpDialog(null)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function HaccpDocForm({ employes, api, type, onClose }) {
  const [employeId, setEmployeId] = useState(employes[0]?.id || '');
  const [motif, setMotif] = useState('');
  const [generating, setGenerating] = useState(false);

  const generate = async () => {
    setGenerating(true);
    try {
      const params = { type_doc: type };
      if (employeId && employeId !== '_blank') params.employe_id = employeId;
      if (motif.trim()) params.motif_complementaire = motif.trim();
      const res = await api.get('/rh/rappel-haccp/pdf', { params, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      const emp = employes.find(e => e.id === employeId);
      const name = emp ? `${emp.prenom}_${emp.nom}` : 'salarie';
      a.download = `${type === 'avertissement' ? 'avertissement' : 'rappel'}_haccp_${name}_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('PDF généré');
      onClose();
    } catch (e) {
      toast.error('Erreur génération PDF');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <Label>Salarié</Label>
        <Select value={employeId} onValueChange={setEmployeId}>
          <SelectTrigger data-testid="haccp-employe"><SelectValue placeholder="Choisir un salarié (ou laisser vide pour blanc)" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="_blank">— Laisser vide (à remplir à la main) —</SelectItem>
            {employes.map(e => <SelectItem key={e.id} value={e.id}>{e.prenom} {e.nom} · {e.poste}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {type === 'avertissement' && (
        <div>
          <Label>Faits reprochés (apparaîtront sur le PDF)</Label>
          <Textarea
            value={motif}
            onChange={e => setMotif(e.target.value)}
            placeholder="Ex : Plan de nettoyage non validé dans l'application le 08/06/2026 malgré contrôle terrain prouvant l'absence de réalisation. Relevé de températures non effectué les 06, 07 et 08/06/2026."
            rows={4}
            data-testid="haccp-motif"
          />
          <p className="text-xs text-muted-foreground mt-1">Soyez précis : dates, faits constatés, références aux modules concernés.</p>
        </div>
      )}
      <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-900">
        <b>Contenu du document :</b> rappel intégral des 8 obligations quotidiennes (températures, décongélation, ouverture/fermeture, plan de nettoyage Chana Poke KPI + TraqFood, rapport de fin de service, températures frigos TraqFood) + conséquences disciplinaires + zone de signature.
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onClose}>Annuler</Button>
        <Button onClick={generate} disabled={generating} data-testid="haccp-generate">
          {generating ? 'Génération...' : <><Download className="w-4 h-4 mr-1" /> Télécharger le PDF</>}
        </Button>
      </div>
    </div>
  );
}

function EmployeCard({ emp, isAdmin, onEdit, onDelete, onCreateAccount }) {
  const cpRestant = (emp.cp_total || 25) - (emp.cp_pris || 0);
  const hasAccount = !!emp.user_id;
  return (
    <Card className="relative overflow-hidden group" data-testid={`emp-card-${emp.prenom}`}>
      <div className="absolute left-0 top-0 bottom-0 w-1.5 rounded-l-lg" style={{ backgroundColor: emp.couleur || '#6B7280' }} />
      <CardContent className="p-5 pl-6">
        <div className="flex items-start gap-4">
          <Avatar prenom={emp.prenom} nom={emp.nom} couleur={emp.couleur} size="lg" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-bold text-lg leading-tight">{emp.prenom} {emp.nom}</h3>
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0">{emp.type_contrat}</Badge>
              {hasAccount && <Badge className="text-[10px] px-1.5 py-0 bg-green-100 text-green-700 hover:bg-green-100"><KeyRound className="w-2.5 h-2.5 mr-0.5" />Compte</Badge>}
            </div>
            <p className="text-sm text-muted-foreground mt-0.5">{emp.poste}</p>
          </div>
          {isAdmin && (
            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              {!hasAccount && (
                <Button variant="ghost" size="icon" className="h-7 w-7 text-blue-600" onClick={onCreateAccount} title="Créer un compte">
                  <KeyRound className="w-3.5 h-3.5" />
                </Button>
              )}
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onEdit}><Pencil className="w-3.5 h-3.5" /></Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive"><Trash2 className="w-3.5 h-3.5" /></Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader><AlertDialogTitle>Supprimer {emp.prenom} ?</AlertDialogTitle>
                  <AlertDialogDescription>Cette action supprimera aussi ses documents et congés.</AlertDialogDescription></AlertDialogHeader>
                  <AlertDialogFooter><AlertDialogCancel>Annuler</AlertDialogCancel><AlertDialogAction onClick={onDelete}>Supprimer</AlertDialogAction></AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}
        </div>
        <div className="grid grid-cols-3 gap-2 mt-4">
          <div className="text-center p-2.5 rounded-lg bg-muted/60">
            <p className="text-lg font-bold tabular-nums">{emp.heures_contrat}h</p>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Contrat</p>
          </div>
          <div className="text-center p-2.5 rounded-lg bg-muted/60">
            <p className="text-lg font-bold tabular-nums">{cpRestant}j</p>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wide">CP rest.</p>
          </div>
          <div className="text-center p-2.5 rounded-lg bg-muted/60">
            <p className="text-lg font-bold tabular-nums">{emp.solde_heures}h</p>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Solde h.</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function AccountForm({ emp, onSave, onClose }) {
  const [email, setEmail] = useState(emp.email || '');
  const [password, setPassword] = useState('');
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error('Email et mot de passe requis'); return; }
    if (password.length < 4) { toast.error('Mot de passe trop court (min 4)'); return; }
    onSave(emp, email, password);
  };
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <p className="text-sm text-muted-foreground">L'employé pourra se connecter pour poser ses congés et voir ses documents.</p>
      <div className="space-y-1.5">
        <Label>Email</Label>
        <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="email@exemple.com" data-testid="account-email" />
      </div>
      <div className="space-y-1.5">
        <Label>Mot de passe initial</Label>
        <Input type="text" value={password} onChange={e => setPassword(e.target.value)} placeholder="Mot de passe" data-testid="account-password" />
        <p className="text-xs text-muted-foreground">L'employé devra le changer à la première connexion</p>
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
        <Button type="submit" data-testid="account-submit">Créer le compte</Button>
      </DialogFooter>
    </form>
  );
}

function EmployeForm({ item, onSave, onClose }) {
  const [form, setForm] = useState(item ? {
    prenom: item.prenom, nom: item.nom || '', poste: item.poste, type_contrat: item.type_contrat,
    heures_contrat: item.heures_contrat, cp_total: item.cp_total, cp_pris: item.cp_pris,
    solde_heures: item.solde_heures, date_embauche: item.date_embauche || '', email: item.email || '', telephone: item.telephone || ''
  } : {
    prenom: '', nom: '', poste: '', type_contrat: 'CDI',
    heures_contrat: 35, cp_total: 25, cp_pris: 0, solde_heures: 0, date_embauche: '', email: '', telephone: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.prenom) { toast.error('Le prénom est requis'); return; }
    if (!form.poste) { toast.error('Le poste est requis'); return; }
    onSave(form);
  };

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label>Prénom *</Label>
          <Input value={form.prenom} onChange={e => set('prenom', e.target.value)} data-testid="emp-prenom" />
        </div>
        <div className="space-y-1.5">
          <Label>Nom</Label>
          <Input value={form.nom} onChange={e => set('nom', e.target.value)} data-testid="emp-nom" />
        </div>
        <div className="space-y-1.5">
          <Label>Poste *</Label>
          <Input value={form.poste} onChange={e => set('poste', e.target.value)} data-testid="emp-poste" />
        </div>
        <div className="space-y-1.5">
          <Label>Type contrat</Label>
          <Select value={form.type_contrat} onValueChange={v => set('type_contrat', v)}>
            <SelectTrigger data-testid="emp-contrat"><SelectValue /></SelectTrigger>
            <SelectContent>
              {TYPES_CONTRAT.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Heures/semaine</Label>
          <Input type="number" value={form.heures_contrat} onChange={e => set('heures_contrat', parseFloat(e.target.value) || 0)} />
        </div>
        <div className="space-y-1.5">
          <Label>CP total/an</Label>
          <Input type="number" value={form.cp_total} onChange={e => set('cp_total', parseInt(e.target.value) || 0)} />
        </div>
        <div className="space-y-1.5">
          <Label>Email</Label>
          <Input type="email" value={form.email} onChange={e => set('email', e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label>Téléphone</Label>
          <Input value={form.telephone} onChange={e => set('telephone', e.target.value)} />
        </div>
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
        <Button type="submit" data-testid="emp-submit">{item ? 'Modifier' : 'Créer'}</Button>
      </DialogFooter>
    </form>
  );
}

// ============== CONGES TAB ==============
function CongesTab({ conges, employes, api, onRefresh, isAdmin, user, myProfile }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filterEmp, setFilterEmp] = useState('all');
  const [filterStatut, setFilterStatut] = useState('all');
  const isEmployee = user?.role !== 'admin';

  const filtered = conges.filter(c => {
    if (filterEmp !== 'all' && c.employe_id !== filterEmp) return false;
    if (filterStatut !== 'all' && c.statut !== filterStatut) return false;
    return true;
  });

  const pendingCount = conges.filter(c => c.statut === 'en_attente').length;

  const handleCreate = async (form) => {
    try {
      await api.post('/rh/conges', form);
      toast.success('Demande de congé créée');
      setDialogOpen(false);
      onRefresh();
    } catch { toast.error('Erreur'); }
  };

  const handleValidate = async (id, statut) => {
    try {
      await api.put(`/rh/conges/${id}/valider`, { statut });
      toast.success(statut === 'valide' ? 'Congé validé' : 'Congé refusé');
      onRefresh();
    } catch { toast.error('Erreur'); }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/rh/conges/${id}`);
      toast.success('Supprimé');
      onRefresh();
    } catch { toast.error('Erreur'); }
  };

  const statutBadge = (s) => {
    if (s === 'valide') return <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Validé</Badge>;
    if (s === 'refuse') return <Badge variant="destructive">Refusé</Badge>;
    return <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">En attente</Badge>;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <Select value={filterEmp} onValueChange={setFilterEmp}>
            <SelectTrigger className="w-[180px]" data-testid="conge-filter-emp"><SelectValue placeholder="Tous" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les employés</SelectItem>
              {employes.map(e => <SelectItem key={e.id} value={e.id}>{e.prenom} {e.nom}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterStatut} onValueChange={setFilterStatut}>
            <SelectTrigger className="w-[160px]" data-testid="conge-filter-statut"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous statuts</SelectItem>
              <SelectItem value="en_attente">En attente ({pendingCount})</SelectItem>
              <SelectItem value="valide">Validé</SelectItem>
              <SelectItem value="refuse">Refusé</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button className="gap-2" onClick={() => setDialogOpen(true)} data-testid="btn-add-conge">
          <Plus className="w-4 h-4" /> Poser un congé
        </Button>
      </div>

      {pendingCount > 0 && (
        <Card className="border-amber-200 bg-amber-50/50 dark:bg-amber-950/20">
          <CardContent className="py-3 px-4 flex items-center gap-2 text-amber-800 dark:text-amber-400">
            <Clock className="w-4 h-4" />
            <span className="text-sm font-medium">{pendingCount} demande{pendingCount > 1 ? 's' : ''} en attente de validation</span>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employé</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Du</TableHead>
                <TableHead>Au</TableHead>
                <TableHead className="text-right">Jours</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Motif</TableHead>
                <TableHead className="w-[120px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Aucun congé</TableCell></TableRow>
              ) : filtered.map(c => (
                <TableRow key={c.id}>
                  <TableCell className="font-medium">{c.employe_nom}</TableCell>
                  <TableCell>{TYPES_CONGE.find(t => t.value === c.type_conge)?.label || c.type_conge}</TableCell>
                  <TableCell>{formatDate(c.date_debut)}</TableCell>
                  <TableCell>{formatDate(c.date_fin)}</TableCell>
                  <TableCell className="text-right tabular-nums font-medium">{c.jours}</TableCell>
                  <TableCell>{statutBadge(c.statut)}</TableCell>
                  <TableCell className="text-muted-foreground text-sm max-w-[150px] truncate">{c.motif}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {c.statut === 'en_attente' && isAdmin && (
                        <>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-green-600" onClick={() => handleValidate(c.id, 'valide')} title="Valider" data-testid={`validate-${c.id}`}>
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600" onClick={() => handleValidate(c.id, 'refuse')} title="Refuser" data-testid={`refuse-${c.id}`}>
                            <X className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      {c.statut === 'en_attente' && isEmployee && (
                        <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 text-xs">En attente de validation</Badge>
                      )}
                      {isAdmin && (
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(c.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Poser un congé</DialogTitle></DialogHeader>
          <CongeForm employes={employes} onSave={handleCreate} onClose={() => setDialogOpen(false)} myProfile={myProfile} isEmployee={isEmployee} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CongeForm({ employes, onSave, onClose, myProfile, isEmployee }) {
  const defaultEmpId = isEmployee && myProfile ? myProfile.id : (employes[0]?.id || '');
  const [form, setForm] = useState({ employe_id: defaultEmpId, type_conge: 'cp', date_debut: '', date_fin: '', jours: 1, motif: '' });
  const [dateDebutOpen, setDateDebutOpen] = useState(false);
  const [dateFinOpen, setDateFinOpen] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    if (form.date_debut && form.date_fin) {
      try {
        const days = differenceInBusinessDays(parseISO(form.date_fin), parseISO(form.date_debut)) + 1;
        set('jours', Math.max(0.5, days));
      } catch {}
    }
  }, [form.date_debut, form.date_fin]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.employe_id || !form.date_debut || !form.date_fin) { toast.error('Remplissez tous les champs'); return; }
    onSave(form);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-3">
        <div className="space-y-1.5">
          <Label>Employé</Label>
          {isEmployee && myProfile ? (
            <Input value={`${myProfile.prenom} ${myProfile.nom || ''}`} disabled className="bg-muted" data-testid="conge-employe-fixed" />
          ) : (
            <Select value={form.employe_id} onValueChange={v => set('employe_id', v)}>
              <SelectTrigger data-testid="conge-employe"><SelectValue /></SelectTrigger>
              <SelectContent>
                {employes.map(e => <SelectItem key={e.id} value={e.id}>{e.prenom} {e.nom}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
        <div className="space-y-1.5">
          <Label>Type</Label>
          <Select value={form.type_conge} onValueChange={v => set('type_conge', v)}>
            <SelectTrigger data-testid="conge-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              {TYPES_CONGE.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Date début</Label>
            <Popover open={dateDebutOpen} onOpenChange={setDateDebutOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="conge-debut">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {form.date_debut ? format(parseISO(form.date_debut), 'dd/MM/yyyy') : 'Début'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar mode="single" selected={form.date_debut ? parseISO(form.date_debut) : undefined}
                  onSelect={d => { if (d) { set('date_debut', toISODateString(d)); setDateDebutOpen(false); }}} locale={fr} />
              </PopoverContent>
            </Popover>
          </div>
          <div className="space-y-1.5">
            <Label>Date fin</Label>
            <Popover open={dateFinOpen} onOpenChange={setDateFinOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="conge-fin">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {form.date_fin ? format(parseISO(form.date_fin), 'dd/MM/yyyy') : 'Fin'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar mode="single" selected={form.date_fin ? parseISO(form.date_fin) : undefined}
                  onSelect={d => { if (d) { set('date_fin', toISODateString(d)); setDateFinOpen(false); }}} locale={fr} />
              </PopoverContent>
            </Popover>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Nombre de jours</Label>
            <Input type="number" step="0.5" value={form.jours} onChange={e => set('jours', parseFloat(e.target.value) || 0)} data-testid="conge-jours" />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Motif</Label>
          <Input value={form.motif} onChange={e => set('motif', e.target.value)} placeholder="Optionnel" data-testid="conge-motif" />
        </div>
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
        <Button type="submit" data-testid="conge-submit">Envoyer la demande</Button>
      </DialogFooter>
    </form>
  );
}

// ============== DOCUMENTS TAB (par employé) ==============
function DocumentsTab({ documents, employes, api, onRefresh, isAdmin, myProfile }) {
  const isEmployee = !isAdmin && myProfile;
  const [selectedEmp, setSelectedEmp] = useState(isEmployee ? myProfile : null);
  const [uploading, setUploading] = useState(false);
  const [uploadCat, setUploadCat] = useState('fiche_paie');
  const [uploadTitre, setUploadTitre] = useState('');
  const fileInputRef = { current: null };

  // Auto-select employee profile for non-admins
  useEffect(() => {
    if (isEmployee && myProfile && !selectedEmp) setSelectedEmp(myProfile);
  }, [isEmployee, myProfile, selectedEmp]);

  const empDocs = selectedEmp ? documents.filter(d => d.employe_id === selectedEmp.id) : [];
  const docsByCategory = {};
  empDocs.forEach(d => {
    const cat = d.categorie || 'autre';
    if (!docsByCategory[cat]) docsByCategory[cat] = [];
    docsByCategory[cat].push(d);
  });

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedEmp) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('employe_id', selectedEmp.id);
      fd.append('categorie', uploadCat);
      fd.append('titre', uploadTitre || file.name);
      await api.post('/rh/documents', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Document ajouté');
      setUploadTitre('');
      onRefresh();
    } catch { toast.error('Erreur upload'); }
    setUploading(false);
    e.target.value = '';
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/rh/documents/${id}`); toast.success('Supprimé'); onRefresh(); } catch { toast.error('Erreur'); }
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(0)} Ko`;
    return `${(bytes / 1048576).toFixed(1)} Mo`;
  };

  if (!selectedEmp) {
    if (isEmployee) return <p className="text-muted-foreground text-center py-8">Aucun profil employé associé à votre compte</p>;
    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">Sélectionnez un employé pour accéder à ses documents</p>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {employes.map(emp => {
            const count = documents.filter(d => d.employe_id === emp.id).length;
            return (
              <Card key={emp.id} className="cursor-pointer hover:border-primary/50 transition-colors" onClick={() => setSelectedEmp(emp)} data-testid={`doc-folder-${emp.prenom}`}>
                <CardContent className="p-4 flex items-center gap-3">
                  <Avatar prenom={emp.prenom} nom={emp.nom} couleur={emp.couleur} size="md" />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold">{emp.prenom} {emp.nom}</p>
                    <p className="text-xs text-muted-foreground">{emp.poste}</p>
                  </div>
                  <Badge variant="secondary" className="shrink-0">{count} doc{count !== 1 ? 's' : ''}</Badge>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {!isEmployee && (
            <Button variant="ghost" size="sm" onClick={() => setSelectedEmp(null)} className="gap-1">
              <X className="w-4 h-4" /> Retour
            </Button>
          )}
          <Avatar prenom={selectedEmp.prenom} nom={selectedEmp.nom} couleur={selectedEmp.couleur} size="sm" />
          <div>
            <span className="font-semibold">{selectedEmp.prenom} {selectedEmp.nom}</span>
            <span className="text-sm text-muted-foreground ml-2">{empDocs.length} document{empDocs.length !== 1 ? 's' : ''}</span>
          </div>
        </div>
      </div>

      {/* Upload zone - admin only */}
      {isAdmin && (
      <Card className="border-dashed">
        <CardContent className="p-4">
          <div className="flex items-end gap-3 flex-wrap">
            <div className="space-y-1 min-w-[140px]">
              <Label className="text-xs">Catégorie</Label>
              <Select value={uploadCat} onValueChange={setUploadCat}>
                <SelectTrigger className="h-9" data-testid="doc-upload-cat"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DOC_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1 flex-1 min-w-[140px]">
              <Label className="text-xs">Titre (optionnel)</Label>
              <Input className="h-9" value={uploadTitre} onChange={e => setUploadTitre(e.target.value)} placeholder="Nom du document" />
            </div>
            <div>
              <input type="file" className="hidden" ref={r => fileInputRef.current = r} onChange={handleUpload} accept=".pdf,.doc,.docx,.jpg,.png,.xlsx" data-testid="doc-file-input" />
              <Button className="gap-2 h-9" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="btn-upload-doc">
                <Upload className="w-4 h-4" /> {uploading ? 'Envoi...' : 'Importer un document'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Documents by category */}
      {empDocs.length === 0 ? (
        <Card className="py-8"><CardContent className="text-center text-muted-foreground"><FileText className="w-10 h-10 mx-auto mb-3 opacity-30" /><p>Aucun document pour cet employé</p></CardContent></Card>
      ) : (
        DOC_CATEGORIES.map(cat => {
          const catDocs = docsByCategory[cat.value];
          if (!catDocs || catDocs.length === 0) return null;
          return (
            <Card key={cat.value}>
              <CardHeader className="py-3 px-4">
                <CardTitle className="text-sm flex items-center gap-2"><FileText className="w-4 h-4" /> {cat.label} ({catDocs.length})</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow><TableHead>Titre</TableHead><TableHead>Taille</TableHead><TableHead>Date</TableHead><TableHead className="w-[80px]">Actions</TableHead></TableRow>
                  </TableHeader>
                  <TableBody>
                    {catDocs.map(d => (
                      <TableRow key={d.id}>
                        <TableCell className="font-medium">{d.titre}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">{formatSize(d.size)}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">{formatDate(d.created_at?.split('T')[0])}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={async () => {
                              try {
                                const res = await api.get(`/rh/documents/${d.id}/download`, { responseType: 'blob' });
                                const blobUrl = URL.createObjectURL(res.data);
                                const a = document.createElement('a');
                                a.href = blobUrl; a.download = d.filename || d.titre || 'document';
                                document.body.appendChild(a); a.click(); a.remove();
                                URL.revokeObjectURL(blobUrl);
                              } catch { toast.error('Telechargement impossible'); }
                            }}><Download className="w-3.5 h-3.5" /></Button>
                            {isAdmin && (
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(d.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          );
        })
      )}
    </div>
  );
}

// ============== REGISTRE TAB ==============
function RegistreTab({ employes, conges }) {
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader><CardTitle className="text-base">Registre du personnel</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead></TableHead>
                <TableHead>Prénom Nom</TableHead>
                <TableHead>Poste</TableHead>
                <TableHead>Contrat</TableHead>
                <TableHead className="text-right">Heures/sem</TableHead>
                <TableHead className="text-right">CP Total</TableHead>
                <TableHead className="text-right">CP Pris</TableHead>
                <TableHead className="text-right">CP Restant</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Téléphone</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {employes.length === 0 ? (
                <TableRow><TableCell colSpan={10} className="text-center py-8 text-muted-foreground">Aucun employé</TableCell></TableRow>
              ) : employes.map(emp => (
                <TableRow key={emp.id}>
                  <TableCell><Avatar prenom={emp.prenom} nom={emp.nom} couleur={emp.couleur} size="sm" /></TableCell>
                  <TableCell className="font-medium">{emp.prenom} {emp.nom}</TableCell>
                  <TableCell>{emp.poste}</TableCell>
                  <TableCell><Badge variant="secondary">{emp.type_contrat}</Badge></TableCell>
                  <TableCell className="text-right tabular-nums">{emp.heures_contrat}h</TableCell>
                  <TableCell className="text-right tabular-nums">{emp.cp_total}j</TableCell>
                  <TableCell className="text-right tabular-nums">{emp.cp_pris}j</TableCell>
                  <TableCell className="text-right tabular-nums font-medium">{(emp.cp_total || 25) - (emp.cp_pris || 0)}j</TableCell>
                  <TableCell className="text-muted-foreground text-sm">{emp.email || '-'}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">{emp.telephone || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}


// ============== MON PROFIL TAB ==============
function MonProfilTab({ profile, conges }) {
  if (!profile) {
    return (
      <Card className="py-12">
        <CardContent className="text-center text-muted-foreground">
          <Users className="w-12 h-12 mx-auto mb-4 opacity-30" />
          <p className="text-lg font-medium">Profil non trouvé</p>
          <p className="text-sm mt-1">Contactez l'administrateur pour lier votre compte.</p>
        </CardContent>
      </Card>
    );
  }

  const cpRestant = (profile.cp_total || 25) - (profile.cp_pris || 0);
  const myConges = conges || [];
  const congesValides = myConges.filter(c => c.statut === 'valide').length;
  const congesEnAttente = myConges.filter(c => c.statut === 'en_attente').length;

  return (
    <div className="space-y-6">
      {/* Fiche employé */}
      <Card data-testid="mon-profil-card">
        <CardContent className="p-6">
          <div className="flex items-start gap-5">
            <Avatar prenom={profile.prenom} nom={profile.nom} couleur={profile.couleur} size="lg" />
            <div className="flex-1">
              <h2 className="text-2xl font-bold">{profile.prenom} {profile.nom || ''}</h2>
              <p className="text-muted-foreground mt-0.5">{profile.poste}</p>
              <div className="flex gap-2 mt-2">
                <Badge variant="secondary">{profile.type_contrat}</Badge>
                {profile.date_embauche && (
                  <Badge variant="outline">Depuis le {formatDate(profile.date_embauche)}</Badge>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Informations détaillées */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Contrat</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Type</span>
              <span className="text-sm font-medium">{profile.type_contrat}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Heures / semaine</span>
              <span className="text-sm font-medium">{profile.heures_contrat}h</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Email</span>
              <span className="text-sm font-medium">{profile.email || '-'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Téléphone</span>
              <span className="text-sm font-medium">{profile.telephone || '-'}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Congés & Solde</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center p-3 rounded-lg bg-muted/60">
                <p className="text-2xl font-bold tabular-nums">{cpRestant}j</p>
                <p className="text-[11px] text-muted-foreground uppercase tracking-wide mt-1">CP restants</p>
              </div>
              <div className="text-center p-3 rounded-lg bg-muted/60">
                <p className="text-2xl font-bold tabular-nums">{profile.cp_pris || 0}j</p>
                <p className="text-[11px] text-muted-foreground uppercase tracking-wide mt-1">CP pris</p>
              </div>
              <div className="text-center p-3 rounded-lg bg-muted/60">
                <p className="text-2xl font-bold tabular-nums">{profile.solde_heures || 0}h</p>
                <p className="text-[11px] text-muted-foreground uppercase tracking-wide mt-1">Solde heures</p>
              </div>
            </div>
            <div className="flex gap-4 mt-4 text-sm">
              <span className="text-muted-foreground">{congesValides} congé{congesValides !== 1 ? 's' : ''} validé{congesValides !== 1 ? 's' : ''}</span>
              {congesEnAttente > 0 && (
                <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">{congesEnAttente} en attente</Badge>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}



// ============== VACANCES CALENDAR ==============
function VacancesCalendar({ api, employes }) {
  const [currentDate, setCurrentDate] = useState(startOfMonth(new Date()));
  const [calData, setCalData] = useState(null);
  const [loading, setLoading] = useState(true);

  const annee = currentDate.getFullYear();
  const mois = currentDate.getMonth() + 1;

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/rh/vacances-calendar?annee=${annee}&mois=${mois}`);
        setCalData(res.data);
      } catch { }
      setLoading(false);
    };
    load();
  }, [api, annee, mois]);

  const daysInMonth = getDaysInMonth(currentDate);
  const firstDayOfWeek = (getDay(currentDate) + 6) % 7; // Monday=0
  const JOURS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

  const isCongeDay = (empId, dayNum) => {
    if (!calData?.conges) return null;
    const dateStr = `${annee}-${String(mois).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
    return calData.conges.find(c => c.employe_id === empId && c.date_debut <= dateStr && c.date_fin >= dateStr);
  };

  const empList = calData?.employes?.length ? calData.employes : employes;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="icon" onClick={() => setCurrentDate(subMonths(currentDate, 1))} data-testid="cal-prev">
          <ChevronLeft className="w-5 h-5" />
        </Button>
        <h3 className="text-lg font-bold capitalize">
          {format(currentDate, 'MMMM yyyy', { locale: fr })}
        </h3>
        <Button variant="ghost" size="icon" onClick={() => setCurrentDate(addMonths(currentDate, 1))} data-testid="cal-next">
          <ChevronRight className="w-5 h-5" />
        </Button>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {loading ? (
            <div className="py-12 text-center text-muted-foreground">Chargement...</div>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2 font-medium sticky left-0 bg-background min-w-[120px] z-10">Employé</th>
                  {Array.from({ length: daysInMonth }, (_, i) => {
                    const dayNum = i + 1;
                    const date = new Date(annee, mois - 1, dayNum);
                    const weekend = isWeekend(date);
                    return (
                      <th key={dayNum} className={`p-1 text-center min-w-[32px] ${weekend ? 'bg-muted/50 text-muted-foreground' : ''}`}>
                        <div className="text-[10px] text-muted-foreground">{JOURS[(firstDayOfWeek + i) % 7]}</div>
                        <div className="font-bold">{dayNum}</div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {empList.map(emp => (
                  <tr key={emp.id} className="border-b hover:bg-muted/10">
                    <td className="p-2 sticky left-0 bg-background z-10">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] font-bold shrink-0" style={{ backgroundColor: emp.couleur || '#6B7280' }}>
                          {(emp.prenom?.[0] || '').toUpperCase()}
                        </div>
                        <span className="font-medium whitespace-nowrap">{emp.prenom}</span>
                      </div>
                    </td>
                    {Array.from({ length: daysInMonth }, (_, i) => {
                      const dayNum = i + 1;
                      const date = new Date(annee, mois - 1, dayNum);
                      const weekend = isWeekend(date);
                      const conge = isCongeDay(emp.id, dayNum);
                      let cellClass = weekend ? 'bg-muted/30' : '';
                      let cellContent = '';
                      if (conge) {
                        const isValide = conge.statut === 'valide';
                        cellClass = isValide ? 'bg-green-500' : 'bg-amber-400';
                        cellContent = (
                          <div className="w-full h-full rounded-sm" title={`${conge.employe_nom} - ${TYPES_CONGE.find(t => t.value === conge.type_conge)?.label || conge.type_conge} (${conge.statut})`}>
                            <span className="text-white text-[9px] font-medium">{isValide ? 'V' : '?'}</span>
                          </div>
                        );
                      }
                      return (
                        <td key={dayNum} className={`p-0.5 text-center ${cellClass}`}>
                          {cellContent}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Legend */}
      <div className="flex items-center gap-4 text-xs text-muted-foreground">
        <div className="flex items-center gap-1.5"><div className="w-4 h-3 rounded-sm bg-green-500" /><span>Validé</span></div>
        <div className="flex items-center gap-1.5"><div className="w-4 h-3 rounded-sm bg-amber-400" /><span>En attente</span></div>
        <div className="flex items-center gap-1.5"><div className="w-4 h-3 rounded-sm bg-muted/50" /><span>Weekend</span></div>
      </div>
    </div>
  );
}
